<template>
  <transition name="slide">
    <div
      class="notification-bar"
      :class="type"
    >
      <div
        class="notification-bar__container"
        :class="{ 'teacher-bar': isTeacherNotification }"
      >
        <div 
          class="notification-bar__content"
          :class="{
            'notification-bar__content--no-action': !actions,
            'notification-bar__content--no-type-block': hideTypeBlock,
          }"
        >
          <rt-picture
            class="notification-bar__icon"
            :src="icon"
            :autosize="false"
            v-if="icon"
          />
          <div
            class="notification-bar__icon-box"
            v-else-if="!hideTypeBlock && (notificationData.text || notificationData.icon)"
          >
            <rt-picture
              class="notification-bar__icon-box-icon"
              :src="notificationData.icon"
              :autosize="false"
              v-if="notificationData.icon"
            />
            <p
              class="notification-bar__icon-box-text"
              v-if="notificationData.text"
            >
              {{ notificationData.text }}
            </p>
          </div>
          <p
            class="notification-bar__content-text"
            v-text="text"
            v-if="text"
          />
          <div
            class="notification-bar__content-html"
            v-else
          >
            <slot />
          </div>
        </div>
        <div 
          v-if="actions"
          class="notification-bar__action"
        >
          <a href="https://readtheory.org/teachers-lounge/important-google-sso-access-changes-for-students">
            <primary-button
              class="notification-bar__action-button"
              @click="handleClickAction"
              v-if="actionText"
            >
              {{ actionText }}
            </primary-button>
          </a>
          <a
            class="notification-bar__action-close"
            @click="closeNotification"
          >
            <rt-picture
              class="notification-bar__action-icon-close"
              src="/assets/app/class/icon-closed.svg"
              :autosize="false"
            />
          </a>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import RtPicture from "./Picture.vue";
import PrimaryButton from "./PrimaryButton.vue";
import { isTeacher } from "common/UserService";
import { constants } from "config";

export default {
  props: {
    type: {
      type: String,
      default: "info",
    },
    text: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    hideTypeBlock: {
      type: Boolean,
      default: true,
    },
    actionText: {
      type: String,
      default: "",
    },
    duration: {
      type: Number,
      default: 0,
    },
    actions: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    RtPicture,
    PrimaryButton,
  },
  data() {
    return {
      isTeacherNotification: isTeacher(),
    };
  },
  computed: {
    notificationData() {
      return constants.NOTIFICATION_TYPE_INFO[this.type] || {};
    },
  },
  mounted() {
    if (this.duration) {
      setTimeout(() => this.closeNotification(), this.duration);
    }
  },
  methods: {
    closeNotification() {
      this.$emit("onClose");
    },
    handleClickAction() {
      this.$emit("onAction");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./variables";

// slide & transition animation
.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease-out;
}

.slide-enter {
  transform: translate3d(0, -100%, 0);
  visibility: visible;

  &-to {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }
}

.slide-leave {
  -webkit-transform: translateZ(0);
  transform: translateZ(0);

  &-to {
    -webkit-transform: translate3d(0, -100%, 0);
    transform: translate3d(0, -100%, 0);
    visibility: hidden;
  }
}

// main
.notification-bar {
  background: $light-yellow;
  padding: 16px 0;

  &.error {
    background: $light-red;
  }

  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 16px;

    &.teacher-bar {
      max-width: $signup-navbar-w;
    }
  }

  &__content,
  &__action {
    display: flex;
    align-items: center;
  }

  .icon-text {
    font-size: $notification_text_font_size;
  }

  &__content {
    flex: 1;
    &-text,
    &-html {
      @extend .icon-text;
      max-width: 80%;
    }
    &--no-type-block {
      .notification-bar__content-text,
      .notification-bar__content-html {
        max-width: none;
        margin-right: 10px;
      }
    }
    &--no-action {
      .notification-bar__content-html,
      .notification-bar__content-text {
        max-width: 90%;
      }
    }
  }

  &__icon {
    margin-right: 16px;
    width: 30px;

    &-box {
      display: flex;
      align-items: center;
      margin-right: 12px;

      &-text {
        margin-left: 12px;
        color: $primary-dark-blue;
        @extend .icon-text;
      }
    }
  }

  &__action {
    &-button {
      height: auto;
      margin: 0 12px 0 0;
      padding: 10px 16px;
      border: 0;
      border-radius: 30px;
      font-size: 12px;
      text-align: center;
      color: $white;
      cursor: pointer;

      &:hover {
        background: $button_hover_bg_color;
      }
    }

    &-close {
      display: inline-block;
      min-width: 10px;
      cursor: pointer;
    }
  }
}

// mobile css
@media (max-width: 767px) {
  .notification-bar__icon-box-text {
    display: none;
  }
}

@media (max-width: 650px) {
  .notification-bar {
    position: relative;

    &__container {
      display: block;
    }

    .notification-bar__content-text,
    .notification-bar__content-html {
      width: 100%;
      max-width: none;
      margin: 0;
    }

    &__action {
      margin-top: 20px;

      &-button {
        width: 100%;
      }

      &-close {
        position: absolute;
        top: 5px;
        right: 15px;
      }
    }
  }
}
</style>
